// ABOUTME: Container component coordinating all modal states and handlers for AdminChildDetail
// ABOUTME: Manages edit behavior, edit child profile, delete child, and delete behavior modals

import React from 'react';
import { Snackbar, Button } from '@mui/material';
import EditBehaviorModal from './EditBehaviorModal';
import EditChildProfileModal from './EditChildProfileModal';
import DeleteChildDialog from './DeleteChildDialog';
import BehaviorDeleteDialog from '../../BehaviorDeleteDialog';
import { BehaviorEventEntry } from '../../BehaviorEventsTable';
import type { ChildDetail, EditEventFormState, EditChildFormState } from '../types';
import { Dayjs } from 'dayjs';

interface ModalsContainerProps {
  // Edit Behavior Modal
  isEditModalOpen: boolean;
  selectedBehavior: BehaviorEventEntry | null;
  editEventData: EditEventFormState;
  dateValidationError: string | null;
  familyChildren: Array<{ id: number; name: string }>;
  familyTimezone?: string;
  onEditModalClose: () => void;
  onEditSave: () => void;
  onEditFormChange: (updates: Partial<EditEventFormState>) => void;
  onEditDateChange: (newValue: Dayjs | null) => void;

  // Delete Behavior Dialog
  isDeleteDialogOpen: boolean;
  onDeleteDialogClose: () => void;
  onDeleteConfirm: () => void;

  // Edit Child Profile Modal
  isEditChildModalOpen: boolean;
  editChildForm: EditChildFormState;
  savingChild: boolean;
  onEditChildModalClose: () => void;
  onEditChildSave: () => void;
  onEditChildFormChange: (updates: Partial<EditChildFormState>) => void;

  // Delete Child Dialog
  isDeleteChildDialogOpen: boolean;
  child: ChildDetail | null;
  deletingChild: boolean;
  onDeleteChildDialogClose: () => void;
  onDeleteChildConfirm: () => void;

  // Snackbar
  snackbar: { open: boolean; message: string; severity: 'success' | 'error' };
  onSnackbarClose: () => void;
}

const ModalsContainer: React.FC<ModalsContainerProps> = ({
  isEditModalOpen,
  selectedBehavior,
  editEventData,
  dateValidationError,
  familyChildren,
  familyTimezone,
  onEditModalClose,
  onEditSave,
  onEditFormChange,
  onEditDateChange,
  isDeleteDialogOpen,
  onDeleteDialogClose,
  onDeleteConfirm,
  isEditChildModalOpen,
  editChildForm,
  savingChild,
  onEditChildModalClose,
  onEditChildSave,
  onEditChildFormChange,
  isDeleteChildDialogOpen,
  child,
  deletingChild,
  onDeleteChildDialogClose,
  onDeleteChildConfirm,
  snackbar,
  onSnackbarClose
}) => {
  return (
    <>
      <EditBehaviorModal
        open={isEditModalOpen}
        selectedBehavior={selectedBehavior}
        editEventData={editEventData}
        dateValidationError={dateValidationError}
        familyChildren={familyChildren}
        familyTimezone={familyTimezone}
        onClose={onEditModalClose}
        onSave={onEditSave}
        onFormChange={onEditFormChange}
        onDateChange={onEditDateChange}
      />

      <BehaviorDeleteDialog
        open={isDeleteDialogOpen}
        behavior={selectedBehavior ? {
          type: selectedBehavior.behaviorType,
          description: selectedBehavior.notes || '',
          createdAt: selectedBehavior.recordedAt,
          loggedBy: selectedBehavior.recordedByUserName
        } : null}
        onClose={onDeleteDialogClose}
        onConfirm={onDeleteConfirm}
      />

      <EditChildProfileModal
        open={isEditChildModalOpen}
        formData={editChildForm}
        saving={savingChild}
        onClose={onEditChildModalClose}
        onSave={onEditChildSave}
        onFormChange={onEditChildFormChange}
      />

      <DeleteChildDialog
        open={isDeleteChildDialogOpen}
        child={child}
        deleting={deletingChild}
        onClose={onDeleteChildDialogClose}
        onConfirm={onDeleteChildConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={onSnackbarClose}
        message={snackbar.message}
        action={
          <Button color="inherit" size="small" onClick={onSnackbarClose}>
            Close
          </Button>
        }
      />
    </>
  );
};

export default ModalsContainer;
